import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader } from 'lucide-react';
import { API_BASE_URL } from '../../config';

export const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      const userId = searchParams.get('userId');

      if (!token || !userId) {
        setError('Invalid verification link');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token, userId }),
        });

        const data = await response.json();

        if (response.ok) {
          setSuccess(true);
          // User will need to log in again after verification
        } else {
          if (data.code === 'TOKEN_EXPIRED') {
            setError('Verification link expired. You can request a new one from your profile.');
          } else if (data.code === 'INVALID_TOKEN') {
            setError('Invalid verification link. Please try again.');
          } else {
            setError(data.error || 'Verification failed');
          }
        }
      } catch (err) {
        setError('Failed to verify email. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Hey Sri Lanka
          </h1>
          <p className="text-gray-600">Email Verification ✉️</p>
        </div>

        {/* Status Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {loading && (
            <div className="text-center py-8">
              <Loader className="w-12 h-12 text-gray-900 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Verifying Your Email
              </h2>
              <p className="text-gray-600">
                Please wait while we verify your email address...
              </p>
            </div>
          )}

          {success && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Email Verified!
              </h2>
              <p className="text-gray-600 mb-6">
                Your email has been successfully verified! Please log in to continue.
              </p>
              <button
                onClick={() => navigate('/email-auth')}
                className="px-6 py-4 bg-gray-900 text-white font-bold rounded-2xl hover:bg-gray-800 active:scale-[0.98] transition-all shadow-lg hover:shadow-xl text-lg"
              >
                Log In
              </button>
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Verification Failed
              </h2>
              <p className="text-gray-600 mb-6">
                {error}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/email-auth')}
                  className="w-full px-6 py-4 bg-gray-900 text-white font-bold rounded-2xl hover:bg-gray-800 active:scale-[0.98] transition-all shadow-lg hover:shadow-xl text-lg"
                >
                  Log In
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="w-full px-6 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all"
                >
                  Go to Home
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

